import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  MissingTranslationHandler,
  MissingTranslationHandlerParams,
  TranslateLoader,
} from '@ngx-translate/core';
import { Observable, catchError, map, of } from 'rxjs';

import { AppVersion } from '../core/constants';
import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class MissingTranslationService implements MissingTranslationHandler {
  constructor(private httpClient: HttpClient) {}

  handleKeys = {};

  /**
   * Only key-shaped strings are worth reporting: anything with whitespace is
   * user data leaked into the translate pipeline (object names, error texts),
   * not a translation key - reporting it would pollute the keyword table.
   */
  private isKeyShaped(key: string): boolean {
    return !/\s/.test(key);
  }

  handle(params: MissingTranslationHandlerParams) {
    const translationKey = params.key;

    if (
      this.isKeyShaped(translationKey) &&
      typeof this.handleKeys[translationKey] === 'undefined'
    ) {
      const headers = new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
      });

      const urlEncodedData = new URLSearchParams();

      this.handleKeys[translationKey] = true;
      const i18nParams = {
        keyword: translationKey,
      };

      Object.keys(i18nParams).forEach((key) => {
        urlEncodedData.append(key, i18nParams[key].toString());
      });

      this.httpClient
        .post('/utils/i18n', urlEncodedData, {
          headers: headers,
        })
        .pipe(
          map((body: any) => {
            return body;
          }),
          catchError(() => of('Error, could not save missing translation :-(')),
        )
        .subscribe();
    }

    return params.key;
  }
}

/**
 * CustomTranslateHttpLoader is a class that implements the TranslateLoader interface.
 * It is responsible for loading translations from the server using HttpClient.
 */
export class CustomTranslateHttpLoader implements TranslateLoader {
  constructor(private http: HttpClient) {}

  /**
   * Retrieves the translation for the specified language from the server.
   * @param lang The language code for the translation.
   * @returns An Observable that emits the translation data.
   */
  getTranslation(lang: string): Observable<any> {
    return this.http.get(`/utils/i18n?lang=${lang}`).pipe(
      map((body: any) => {
        return body;
      }),
      catchError(() => of('Error, could not load translation :-(')),
    );
  }
}
